<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  badgeCount: { type: [Number, String], default: 0 },
  description: { type: String, default: '' },
  subgroup: { type: Boolean, default: false },
  pipeline: { type: Boolean, default: false },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ltr:pl-2 rtl:pr-2 rtl:mr-3 ltr:ml-3 relative text-n-slate-11 child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0 min-w-0"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="flex gap-2 px-2 py-1 rounded-lg hover:bg-gradient-to-r from-transparent via-n-slate-3/70 to-n-slate-3/70 group min-w-0"
      :class="[
        description
          ? 'min-h-9 items-start'
          : 'h-8 items-center',
        subgroup ? 'ltr:pl-8 rtl:pr-8' : '',
        active && pipeline ? 'text-n-slate-12 bg-slate-800 rounded-lg' : '',
        active && !pipeline ? 'text-n-slate-12 bg-n-alpha-2 active' : '',
      ]"
    >
      <component
        :is="component"
        v-if="shouldRenderComponent"
        v-bind="{ label, icon, active, badgeCount }"
      />
      <template v-else>
        <span v-if="icon" class="size-4 grid place-content-center rounded-full flex-shrink-0 mt-1">
          <Icon :icon="icon" class="size-4 inline-block" />
        </span>
        <div class="flex-1 min-w-0">
          <div class="truncate text-sm" :class="active ? 'font-medium text-n-slate-12' : 'text-n-slate-11'">
            {{ label }}
          </div>
          <div v-if="description" class="truncate text-[10px] text-n-slate-10 leading-3 mt-0.5">
            {{ description }}
          </div>
        </div>
        <SidebarUnreadBadge :count="badgeCount" />
      </template>
    </component>
  </Policy>
</template>
